import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import { ArticleSchema, BreadcrumbSchema, WebPageSchema } from '@/components/feature/SEOSchema';
import { articles } from '@/mocks/articles';
import { recommendedSites } from '@/mocks/home';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articles[slug] : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">返回首页</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageMeta
        title={`${article.title} - 看片网站推荐2025`}
        description={article.excerpt}
        keywords={`看片网站,${article.category},在线观影,免费看片`}
        canonicalUrl={`https://wfsqdz.cn/article/${article.slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
        ogUrl={`https://wfsqdz.cn/article/${article.slug}`}
        ogImage={article.coverUrl}
      />
      <ArticleSchema
        title={article.title}
        description={article.excerpt}
        datePublished={article.date}
        dateModified="2025-08-12"
        url={`https://wfsqdz.cn/article/${article.slug}`}
        imageUrl={article.coverUrl}
      />
      <WebPageSchema
        title={article.title}
        description={article.excerpt}
        url={`https://wfsqdz.cn/article/${article.slug}`}
      />
      <BreadcrumbSchema
        items={[
          { name: '首页', url: 'https://wfsqdz.cn/' },
          { name: '精选文章', url: 'https://wfsqdz.cn/' },
          { name: article.title, url: `https://wfsqdz.cn/article/${article.slug}` },
        ]}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <article>
            <header className="relative w-full h-[320px] md:h-[420px] overflow-hidden">
              <img
                src={article.coverUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                <div className="max-w-4xl mx-auto">
                  <nav className="flex items-center gap-2 text-sm text-background-300 mb-4" aria-label="面包屑导航">
                    <Link to="/" className="hover:text-background-100 transition-colors">首页</Link>
                    <i className="ri-arrow-right-s-line text-xs"></i>
                    <Link to="/" className="hover:text-background-100 transition-colors">精选文章</Link>
                    <i className="ri-arrow-right-s-line text-xs"></i>
                    <span className="text-background-100">{article.category}</span>
                  </nav>
                  <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-background-50 leading-tight mb-3">
                    {article.title}
                  </h1>
                  <div className="flex items-center gap-4 text-sm text-background-300">
                    <span>{article.date}</span>
                    <span>·</span>
                    <span>{article.readTime}</span>
                    <span>·</span>
                    <span>{article.category}</span>
                  </div>
                </div>
              </div>
            </header>

            <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
              <div className="prose-custom">
                {article.content.map((block, i) => {
                  if (block.type === 'heading' && block.text) {
                    return (
                      <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === 'paragraph' && block.text) {
                    return (
                      <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                        {block.text}
                      </p>
                    );
                  }
                  if (block.type === 'list' && block.items) {
                    return (
                      <ul key={i} className="space-y-2 mb-5 pl-5">
                        {block.items.map((item, j) => (
                          <li key={j} className="text-sm md:text-base text-foreground-600 leading-relaxed flex items-start gap-2">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0"></span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return null;
                })}
              </div>

              <div className="mt-14 p-6 bg-background-100/60 rounded-xl border border-background-200/70">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                  相关推荐站点
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {recommendedSites.slice(0, 3).map((site) => (
                    <Link
                      key={site.id}
                      to={`/category/${site.category === '电影' ? 'movie' : site.category === '剧集' ? 'series' : site.category === '综艺' ? 'variety' : site.category === '动漫' ? 'anime' : 'documentary'}`}
                      className="flex items-center gap-3 p-3 rounded-lg bg-background-50 hover:bg-primary-50/30 transition-colors cursor-pointer"
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                        <img src={site.logoUrl} alt={site.name} className="w-full h-full object-cover" width="40" height="40" />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-foreground-800 block">{site.name}</span>
                        <span className="text-xs text-foreground-400">{site.rating} 分</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}